import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CurrentUser,
  RequestOtp,
  UserVerifyOTP,
} from "../../../utils/apiCalls/auth";
import axiosConfig from "../../../utils/axiosConfig";
import { API_CONSTANTS } from "../../../utils/api-constant";
import { useToast } from "../../../utils/ToastContext";

const initialValues = {
  name: "",
  userName: "",
  email: "",
};

const useSuperAdminEditProfile = () => {
  const [errors, setErrors] = useState(initialValues);
  const [formData, setFormData] = useState(initialValues);
  const [secondaryEmails, setSecondaryEmails] = useState([]);
  const [originalFormData, setOriginalFormData] = useState(initialValues);
  const [originalSecondaryEmails, setOriginalSecondaryEmails] = useState([]);
  const [newSecondaryEmail, setNewSecondaryEmail] = useState("");
  const [editingIndex, setEditingIndex] = useState(null);
  const [editingEmail, setEditingEmail] = useState("");
  const { showToast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [otp, setOtp] = useState("");
  const profilePayloadRef = useRef(null);
  const autoSubmitRef = useRef(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchProfileDetails();
  }, []);

  const fetchProfileDetails = async () => {
    try {
      const res = await CurrentUser();
      const { data, message, statusCode } = res;
      if (statusCode === 200) {
        const superAdminData = data.currentSuperAdminModel;
        const initialData = {
          name: superAdminData?.name || "",
          userName: superAdminData?.userName || "",
          email: superAdminData?.email || "",
        };
        const initialEmails = superAdminData?.secondaryEmails || [];
        // Create completely independent copies for both current and original states
        setFormData({ ...initialData });
        setSecondaryEmails([...initialEmails]);
        setOriginalFormData({ ...initialData });
        setOriginalSecondaryEmails([...initialEmails]);
      } else {
        console.error("Error fetching profile details:", message);
      }
    } catch (error) {
      console.error("Error fetching profile details:", error?.message);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
    setErrors({
      ...errors,
      [name]: "",
    });
  };

  const handleAddSecondaryEmail = () => {
    if (!newSecondaryEmail.trim()) {
      showToast("Please enter an email address", "error");
      return;
    }

    if (!/\S+@\S+\.\S+/.test(newSecondaryEmail)) {
      showToast("Please enter a valid email address", "error");
      return;
    }

    if (secondaryEmails.includes(newSecondaryEmail)) {
      showToast("This email already exists", "error");
      return;
    }

    setSecondaryEmails([...secondaryEmails, newSecondaryEmail]);
    setNewSecondaryEmail("");
  };

  const handleDeleteSecondaryEmail = (index) => {
    setSecondaryEmails(secondaryEmails.filter((_, i) => i !== index));
  };

  const handleEditSecondaryEmail = (index) => {
    setEditingIndex(index);
    setEditingEmail(secondaryEmails[index]);
  };

  const handleSaveEditSecondaryEmail = (index) => {
    if (!editingEmail.trim()) {
      showToast("Please enter an email address", "error");
      return;
    }

    if (!/\S+@\S+\.\S+/.test(editingEmail)) {
      showToast("Please enter a valid email address", "error");
      return;
    }

    // Check for duplicates (excluding the current email being edited)
    const isDuplicate = secondaryEmails.some(
      (email, i) => i !== index && email === editingEmail
    );
    if (isDuplicate) {
      showToast("This email already exists", "error");
      return;
    }

    const updatedEmails = [...secondaryEmails];
    updatedEmails[index] = editingEmail;
    setSecondaryEmails(updatedEmails);
    setEditingIndex(null);
    setEditingEmail("");
  };

  const handleCancelEditSecondaryEmail = () => {
    setEditingIndex(null);
    setEditingEmail("");
  };

  const isFormChanged = () => {
    // Helper function to safely get trimmed string
    const getTrimmed = (val) => (val ? String(val).trim() : "");

    // Check if any form field has changed (compare trimmed values)
    const nameChanged = getTrimmed(formData.name) !== getTrimmed(originalFormData.name);
    const userNameChanged = getTrimmed(formData.userName) !== getTrimmed(originalFormData.userName);
    const emailChanged = getTrimmed(formData.email) !== getTrimmed(originalFormData.email);

    const formFieldsChanged = nameChanged || userNameChanged || emailChanged;

    // Check if secondary emails have changed (deep comparison with sorting)
    const currentSorted = secondaryEmails.toSorted();
    const originalSorted = originalSecondaryEmails.toSorted();
    const emailsChanged =
      secondaryEmails.length !== originalSecondaryEmails.length ||
      JSON.stringify(currentSorted) !== JSON.stringify(originalSorted);

    return formFieldsChanged || emailsChanged;
  };

  const validateFields = () => {
    let errObj = { ...initialValues };

    if (!formData.name) {
      errObj.name = "This field is required";
    } else if (!/^[A-Za-z\s]+$/.test(formData.name)) {
      errObj.name = "Only alphabets and spaces are allowed";
    } else {
      errObj.name = "";
    }

    if (!formData.userName) {
      errObj.userName = "This field is required";
    } else if (!/^[\w@$!#*&\s.-]{3,16}$/.test(formData.userName)) {
      errObj.userName = "Minimum 3 and Maximum 16 characters only";
    } else {
      errObj.userName = "";
    }

    if (!formData.email) {
      errObj.email = "This field is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errObj.email = "Email is invalid";
    } else {
      errObj.email = "";
    }

    setErrors(errObj);
    const isValid = Object.values(errObj).every((x) => x === "" || x === null);
    return isValid;
  };

  const handleSubmit = () => {
    handleRequestOtp();
  };

  // Auto-verify when all OTP digits are entered.
  // isLoading is intentionally excluded from deps: handleVerifyOtp sets it
  // itself, so depending on it here would re-run/cleanup this effect mid-flight
  // (marking the in-flight call "stale" before its response even arrives) and
  // retrigger verification with the same OTP in an infinite loop. autoSubmitRef
  // guards re-entrancy instead, without causing the effect to re-run.
  useEffect(() => {
    let ignore = false;
    if (otp.length === 6 && showOtpModal && !autoSubmitRef.current) {
      autoSubmitRef.current = true;
      handleVerifyOtp(() => ignore).finally(() => {
        autoSubmitRef.current = false;
      });
    }
    return () => {
      ignore = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otp, showOtpModal]);

  const handleRequestOtp = async () => {
    setIsLoading(true);
    if (validateFields()) {
      const payload = {
        name: formData.name,
        userName: formData.userName,
        email: formData.email,
        secondaryEmails: secondaryEmails,
      };

      // Store payload for later use
      profilePayloadRef.current = payload;

      // Request OTP
      try {
        const res = await RequestOtp();
        setIsLoading(false);
        const { statusCode, message } = res;
        if (statusCode === 200) {
          setShowOtpModal(true);
          setOtp("");
        } else {
          showToast(message || "Failed to request OTP", "error");
        }
      } catch (error) {
        setIsLoading(false);
        showToast(error?.message || "Failed to request OTP", "error");
      }
    } else {
      setIsLoading(false);
    }
  };

  const handleVerifyOtp = async (isStale = () => false) => {
    setIsLoading(true);

    if (otp.length !== 6) {
      setIsLoading(false);
      return;
    }

    // Verify OTP with authenticationType = 3
    const verifyPayload = {
      otp,
      authenticationType: 3,
    };

    try {
      const res = await UserVerifyOTP(verifyPayload, formData.email, 3);
      if (isStale()) {
        setIsLoading(false);
        return;
      }
      const { statusCode, message } = res;
      if (statusCode === 200) {
        // OTP verified, now call UpdateSuperAdminProfile
        try {
          const updateRes = await axiosConfig.post(
            API_CONSTANTS.UPDATE_SUPER_ADMIN_PROFILE,
            profilePayloadRef.current
          );
          if (isStale()) {
            setIsLoading(false);
            return;
          }
          setIsLoading(false);
          const { statusCode: updateStatusCode, message: updateMessage } = updateRes.data;
          if (updateStatusCode === 200) {
            showToast(updateMessage || "Profile updated successfully", "success");
            setShowOtpModal(false);
            setTimeout(() => {
              navigate("/profile-details");
            }, 1500);
          } else {
            showToast(updateMessage || "Failed to update profile", "error");
            setShowOtpModal(false);
            setOtp("");
          }
        } catch (updateError) {
          setIsLoading(false);
          if (isStale()) return;
          const errorMessage = updateError.response?.data?.message || "An error occurred";
          showToast(errorMessage, "error");
          setShowOtpModal(false);
          setOtp("");
        }
      } else {
        setIsLoading(false);
        showToast(message || "OTP verification failed", "error");
        setOtp("");
      }
    } catch (error) {
      setIsLoading(false);
      if (isStale()) return;
      showToast(error?.message || "OTP verification failed", "error");
      setOtp("");
    }
  };

  const handleCancelOtp = () => {
    setShowOtpModal(false);
    setOtp("");
    profilePayloadRef.current = null;
  };

  const handleCancel = () => {
    navigate("/profile-details");
  };

  return {
    errors,
    formData,
    secondaryEmails,
    newSecondaryEmail,
    setNewSecondaryEmail,
    editingIndex,
    editingEmail,
    setEditingEmail,
    isLoading,
    showOtpModal,
    otp,
    setOtp,
    handleChange,
    handleAddSecondaryEmail,
    handleDeleteSecondaryEmail,
    handleEditSecondaryEmail,
    handleSaveEditSecondaryEmail,
    handleCancelEditSecondaryEmail,
    isFormChanged,
    handleSubmit,
    handleVerifyOtp,
    handleCancelOtp,
    handleCancel,
  };
};

export default useSuperAdminEditProfile;
